import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { UiService } from '@services/ui.service';
import { ReportService } from '@services/report.service';
import { ReportData } from '@classes/report/report-data';
import { ReportDataType } from '@enums/report-data-type.enum';
import { Partition } from '@classes/simulation/partition';
import { ChartType } from '@enums/chart-type.enum';
import { ReportTypeCode } from '@enums/report-type-code.enum';
import { ReportDataset } from '@classes/report/report-dataset';
import { CustomBaseComponent } from '../custom-base/custom-base.component';
import { ApiText } from '@classes/api-text';
import { ReportType } from '@classes/report/report-type';

@Component({
    selector: 'app-custom-compare',
    templateUrl: './custom-compare.component.html',
    styleUrls: ['./custom-compare.component.scss']
})
export class CustomCompareComponent extends CustomBaseComponent implements OnInit, OnDestroy {
    private reportDataSub: Subscription;
    isReportReady: boolean;

    constructor(
        protected uiSrv: UiService,
        protected reportSrv: ReportService
    ) {
        super(uiSrv, reportSrv);
        this.dataType = ReportDataType.percent;
    }

    ngOnInit() {
        this.initSubscriptions();
        if (this.customReport.partitionsCodes && this.customReport.partitionsCodes.length) {
            this.isReportReady = true;
        }
    }

    onSelectPartition(selectedCodes: string[]): void {
        this.customReport.partitionsCodes = [...selectedCodes];

        this.editCustomReport();
    }

    private editCustomReport(): void {
        if (this.customReport.partitionsCodes && this.customReport.partitionsCodes.length) {
            this.customReport.stackedPartitions = this.simulations.map(s => {
                return {
                    label: s.userText.shortLabel,
                    partitions: s.partitions.filter(p => this.customReport.partitionsCodes.indexOf(p.partitionDetails.code) !== -1)
                };
            });

            this.generateReportItems();
            this.isReportReady = true;
        } else {
            this.customReport.partitionsCodes = null;
            this.customReport.articleType = null;
            this.customReport.stackedPartitions = null;
            this.isReportReady = false;
        }

        this.reportSrv.editCustomReport(this.customReport);
    }

    protected generateReportItems(): void {
        const customReportType: ReportType = {
            code: ReportTypeCode.custom,
            text: new ApiText(this.uiSrv.translate('reports.categories.custom.shortLabel'), ' ', '', '')
        };
        const groupNumber: number = 1000 * (this.customReport.id + 1);
        const selectedYears: string[] = this.customReport.partitionsCodes.length > 1 ? [this.referenceYear] : this.years;

        if (this.customReport.partitionsCodes.length > 1)  {
            this.generateMultipartsReportItems(groupNumber, customReportType, selectedYears);
        } else {
            this.generateSinglepartsReportItems(groupNumber, customReportType, selectedYears);
        }

        this.generateDatatable(groupNumber + 1, customReportType, selectedYears);
    }

    private generateSinglepartsReportItems(groupNumber: number, customReportType: ReportType, selectedYears: string[]): void {
        this.customReport.reportItems = [{
            title: new ApiText(' ', this.uiSrv.translate('reports.barChart.groups.fullLabel'),
                        `${customReportType.text.shortLabel} ${this.customReport.id + 1}`, ''),
            group: groupNumber,
            reportType: customReportType,
            selectedVariationCode: 'barChart',
            variations: [
                {
                    code: 'barChart',
                    displayType: ChartType.bar,
                    dataType: ReportDataType.amount,
                    labels: this.customReport.stackedPartitions.map(s => s.label),
                    axesLabels: {
                        categories: this.uiSrv.translate('reports.barChart.groups.shortLabel'),
                        data: this.uiSrv.translate('reports.barChart.inhabitantAmount.shortLabel')
                    },
                    stacks: selectedYears.map(year => {
                        return {
                            name: year,
                            datasets: this.generateDatasetsByYear(year, ReportDataType.amount)
                        };
                    })
                }
            ]
        }];
    }

    private generateMultipartsReportItems(groupNumber: number, customReportType: ReportType, selectedYears: string[]): void {
        this.customReport.reportItems = [{
            title: new ApiText(' ', this.uiSrv.translate('reports.barChart.partitions.fullLabel'),
                        `${customReportType.text.shortLabel} ${this.customReport.id + 1}`, ''),
            group: groupNumber,
            reportType: customReportType,
            selectedVariationCode: 'barChart',
            variations: [
                {
                    code: 'barChart',
                    displayType: ChartType.bar,
                    dataType: ReportDataType.amount,
                    labels: this.customReport.stackedPartitions[0].partitions.map(p => p.partitionDetails.text.shortLabel),
                    axesLabels: {
                        categories: this.uiSrv.translate('reports.barChart.partitions.shortLabel'),
                        data: this.uiSrv.translate('reports.barChart.inhabitantAmount.shortLabel')
                    },
                    stacks: this.customReport.stackedPartitions.map(s => {
                        return {
                            name: s.label,
                            datasets: this.generateDatasetsByPartition(s.partitions)
                        };
                    })
                }
            ]
        }, {
            title: new ApiText(' ', this.uiSrv.translate('reports.barChart.groups.fullLabel'),
                        `${customReportType.text.shortLabel} ${this.customReport.id + 1}`, ''),
            group: groupNumber,
            reportType: customReportType,
            selectedVariationCode: 'horizontalBar',
            variations: [
                {
                    code: 'horizontalBar',
                    displayType: ChartType.horizontalBar,
                    dataType: ReportDataType.percent,
                    labels: this.customReport.stackedPartitions.map(s => s.label),
                    axesLabels: {
                        categories: this.uiSrv.translate('reports.barChart.groups.shortLabel'),
                        data: this.uiSrv.translate('reports.barChart.percents.shortLabel')
                    },
                    stacks: selectedYears.map(year => {
                        return {
                            name: year,
                            datasets: this.generateDatasetsByYear(year, ReportDataType.percent)
                        };
                    })
                }
            ]
        }];
    }

    private generateDatatable(groupNumber: number, customReportType: ReportType, selectedYears: string[]): void {
        const reportItem = {
            title: new ApiText(' ', this.uiSrv.translate('reports.barChart.groups.fullLabel'),
                        `${customReportType.text.shortLabel} ${this.customReport.id + 1}`, ''),
            group: groupNumber,
            reportType: customReportType,
            selectedVariationCode: 'datatable',
            variations: [
                {
                    code: 'datatable',
                    displayType: ChartType.datatable,
                    dataType: ReportDataType.amount,
                    labels: this.customReport.stackedPartitions.map(s => s.label),
                    axesLabels: {
                        categories: this.uiSrv.translate('reports.barChart.groups.shortLabel'),
                        data: this.uiSrv.translate('reports.barChart.inhabitantAmount.shortLabel')
                    },
                    stacks: selectedYears.map(year => {
                        return {
                            name: year,
                            datasets: this.generateDatasetsByYear(year, ReportDataType.amount)
                        };
                    })
                }
            ]
        };

        this.customReport.reportItems.push(reportItem);
    }

    private generateTranslatedText(key: string): ApiText {
        return new ApiText(
            this.uiSrv.translate(key + '.shortLabel'),
            this.uiSrv.translate(key + '.fullLabel'),
            this.uiSrv.translate(key + '.summary')
        );
    }

    private generateDatasetsByPartition(partitions: Partition[]): ReportDataset[] {
        const data: number[] = partitions.map(partition => {
            return partition.simulationValues.years
            .find(y => y.year.toString() === this.referenceYear).amount;
        });

        return [{
            data: data
        }];
    }

    private generateDatasetsByYear(year: string, dataType: ReportDataType): ReportDataset[] {
        const datasets: ReportDataset[] = this.customReport.stackedPartitions[0].partitions
            .map(p => {
                return {
                    label: p.partitionDetails.text.shortLabel,
                    data: []
                };
            });

        datasets.map(dataset => {
            dataset.data = this.customReport.stackedPartitions
                .reduce((accumulator, stack) => {
                    const partYearFound = stack.partitions
                        .find(p => p.partitionDetails.text.shortLabel === dataset.label)
                        .simulationValues.years
                        .find(y => y.year.toString() === year);
                    const value = dataType === ReportDataType.percent ? partYearFound.ratio : partYearFound.amount;

                    return [...accumulator, value];
                }, []);
        });

        return datasets;
    }

    private initSubscriptions(): void {
        this.reportDataSub = this.reportSrv.reportDataSubject
            .subscribe(reportData => {
                if (reportData) {
                    this.initData(reportData);
                }
            });
    }

    private initData(reportData: ReportData): void {
        this.simulations = reportData.simulations;
        this.partitions = reportData.partitions;
        this.referenceYear = reportData.years[reportData.years.length - 1].toString();
        this.years = reportData.years.map(y => y.toString());

        this.rootPartition = this.partitions.find(p => p.partitionDetails.articleType === this.customReport.articleType);
    }

    ngOnDestroy(): void {
        if (this.reportDataSub) {
            this.reportDataSub.unsubscribe();
        }
    }
}
